import * as React from 'react';
import { ServiceContext } from './ServiceContext';
import { Service } from './types';
import { UniversalLoginApiWrapper } from '@apis/universalLogin/UniversalLoginApiWrapper';
import { MockUniversalLoginApi } from '@apis/universalLogin/__mocks__/MockUniversalLoginApi';
import { MockULRpcSDKEffect } from '@apis/universalLogin/__mocks__/MockULRpcSDKEffect';
import { MessagingClient } from '@services/messagingClient/MessagingClient';
import { PerformanceService } from '@services/performance/PerformanceService';
import { WebAnalyticsService } from '@services/analytics/WebAnalyticsService';
import { ADPLogger } from '@services/logger/ADPLogger';
import { ADPMeasurement } from '@services/measurement/ADPMeasurement';
import { ULRequestUniversalLoginApiClient } from '@ul/api';
import { ULRpcSDKEffectService } from '@services/sdkEffect/ULRpcSDKEffectService';
import { ConsoleLogger } from '@services/logger/ConsoleLogger';
import { ULRpcCaller } from '@services/rpcCaller';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { StaticCountryService } from '@services/country/StaticCountryService';
import { useMemberContext } from '@contexts/member/hooks/useMemberContext';
import { HadoopMessenger } from '@services/hadoopMessage/HadoopMessenger';
import { getRpcCaller } from '@services/getRpcCaller';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useExperiment } from '@contexts/experiment/useExperiment';
import { universalLoginExperiment } from '@experiments/universalLogin';
import { useDeviceIntelligence } from '@contexts/deviceIntelligence/hooks/useDeviceIntelligence';
import { WebAnalyticsProvider } from '@agoda/analytics-data-acquisition-react';
import { isMLDCDomain } from '@contexts/app/hooks/useIsYCS';
import { WhitelabelId } from '@whitelabel/WhitelabelId';
import { isRocketmilesR2 } from '@utils/isRocketmilesLegacy';

/**
 * provide services via ServiceContext
 * - loginService
 * - performanceService
 * - logger
 * - measurement
 * - webAnalyticsService
 * - sdkEffect
 *
 * @requires appContext
 * @param Component
 */
export function withService<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
    const ComponentWithService: React.FC<TProps> = props => {
        // tslint:disable: react-hooks-nesting
        const appContext = useAppContext();
        const appSetting = useAppSetting();
        const { member } = useMemberContext();
        const experiments = useExperiment(universalLoginExperiment);
        const deviceIntelligenceContext = useDeviceIntelligence();
        const { appPlatform, rpcId, ulApiUrl, iframeMode, languageId, origin, appId, whitelabelId } = appContext;

        /* one-time initial services */
        const services: Service = React.useMemo(() => {
            /* ADP messaging */
            const messagingClient = MessagingClient.instance;
            const logger = __QA__ || __PROD__ ? new ADPLogger(messagingClient, appContext) : ConsoleLogger;
            const measurement = new ADPMeasurement(messagingClient, appContext);
            const messenger = new HadoopMessenger(messagingClient, appContext);

            /* performance client */
            // FIXME: theoretically this should be update when member updated
            const performanceService = new PerformanceService(messagingClient, appContext, member);

            /* web analytics */
            const webAnalyticsService = new WebAnalyticsService(messagingClient);

            /* ycs client */
            const isYcsMldc = __ENV__ === 'prod' && isMLDCDomain && (appId === 'ycs' || appId === 'ycsApp');

            /* ul client */
            const ulClient = new ULRequestUniversalLoginApiClient(ulApiUrl, isYcsMldc);

            /* service */
            const loginService =
                __MOCK__ || __MOCKAPI__
                    ? new MockUniversalLoginApi()
                    : new UniversalLoginApiWrapper(
                          appContext,
                          measurement,
                          logger,
                          ulClient,
                          deviceIntelligenceContext
                      );

            // TODO: clean this up once R2 migration is completed
            // we dont have to pass parentWhitelistedOrigin for R2UAT
            // ROCKETMILES, we dont need to pass parentWhitelistedOrigin for R2 rocketmiles domain
            const parentWhitelistedOrigin =
                whitelabelId === WhitelabelId.ROCKETMILESUAT ||
                (whitelabelId === WhitelabelId.ROCKETMILES && isRocketmilesR2())
                    ? ''
                    : appSetting.tokenGrantType?.parentWhitelistedOrigin;

            /* sdk effect */
            const rpcCaller = getRpcCaller(
                appPlatform,
                rpcId,
                iframeMode,
                parentWhitelistedOrigin,
                appSetting.tokenGrantType?.useExactUrl
            );

            const sdkEffect = new (__MOCK__ ? MockULRpcSDKEffect : ULRpcSDKEffectService)(
                rpcCaller as ULRpcCaller,
                measurement,
                logger,
                appId
            );

            /* country */
            const countryService = new StaticCountryService(languageId, origin);

            const allServices: Service = {
                loginService,
                performanceService,
                logger,
                measurement,
                webAnalyticsService,
                sdkEffect,
                countryService,
                messenger,
            };
            return allServices;
        }, []);

        // tslint:enable: react-hooks-nesting
        return (
            <ServiceContext.Provider value={services}>
                <WebAnalyticsProvider webAnalytics={services.webAnalyticsService.getModule()}>
                    <Component {...props} />
                </WebAnalyticsProvider>
            </ServiceContext.Provider>
        );
    };

    ComponentWithService.displayName = `${Component.displayName || 'Component'}WithService`;

    return ComponentWithService;
}
